import React from "react";
import BlankState from "@ui/BlankStateV2";
import { Dashboard, NoData } from "@ui/Illustration/react";
import t from "../../../../../../../../../../i18n";
import type { FaultHistoryGroup } from "../../../../../../../../../../api/events/histories";
import { css } from "@linaria/core";
import { colors, spacings } from "@ui/Theme";
import type { Project } from "../../../../../../../../../../api/useProjects";
import { type FaultEvent } from "../../../../../../../../../../api/useFaultEvents";
import { getFaultEventScope } from "diagnostics-domain/faultEvent";
import Filters, { useEventHistoryFilter } from "../../../../../components/SidePanel/Filters";
import HistoryContent from "../../../../../components/SidePanel/HistoryContent";
const SidePanelHistory = ({
  project,
  faultEvent,
  faultEventsHistory,
}: {
  project: Project;
  faultEvent: FaultEvent;
  faultEventsHistory: FaultHistoryGroup[];
}) => {
  const [filterValues, setFilterValues] = useEventHistoryFilter({
    scope: getFaultEventScope({
      id: faultEvent.id,
    }),
  });

  const projectTypes = React.useMemo(
    () => faultEventsHistory.flatMap(history => (history.typeId ? [history.typeId] : [])),
    [faultEventsHistory],
  );
  const projectSites = React.useMemo(
    () => faultEventsHistory.flatMap(history => (history.siteId ? [history.siteId] : [])),
    [faultEventsHistory],
  );

  const filterFaultEvents = React.useCallback(
    (events: FaultHistoryGroup["events"]) =>
      events.filter(fe => {
        const statusMatches =
          filterValues.eventStatus.length === 0 || filterValues.eventStatus.includes(fe.status);
        const detectionMatches =
          filterValues.detection?.length === 0 ||
          (fe.detection !== null && filterValues.detection?.includes(fe.detection.toString()));

        return statusMatches && detectionMatches;
      }),
    [filterValues],
  );

  const filterByProject = React.useCallback(
    (history: FaultHistoryGroup) => {
      const typeMatches =
        filterValues.projectType.length === 0 ||
        (history.typeId !== null && filterValues.projectType.includes(history.typeId));
      const siteMatches =
        filterValues.projectSite.length === 0 ||
        (history.siteId !== null && filterValues.projectSite.includes(history.siteId));

      return typeMatches && siteMatches && history.events.length > 0;
    },
    [filterValues.projectType, filterValues.projectSite],
  );

  const filteredHistory = React.useMemo(() => {
    const hasFiltersOnFaultFpisode =
      filterValues.eventStatus.length > 0 || (filterValues.detection?.length ?? 0) > 0;

    const sortedFilteredHistory = faultEventsHistory
      .map(history => {
        const filteredFaultEvents = hasFiltersOnFaultFpisode
          ? filterFaultEvents(history.events)
          : history.events;

        const sortedFaultEvents = filteredFaultEvents.sort(
          (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime(),
        );

        return {
          ...history,
          events: sortedFaultEvents,
        };
      })
      .filter(filterByProject)
      .sort((a, b) => a.projectName.localeCompare(b.projectName));
    const projectHome = sortedFilteredHistory.find(history => history.projectName === project.name);

    if (projectHome) {
      return [
        {
          ...projectHome,
          projectName: t("project.fault_event.side_panel.history.current_project"),
        },
        ...sortedFilteredHistory.filter(history => history !== projectHome),
      ];
    }

    return sortedFilteredHistory;
  }, [project, faultEventsHistory, filterValues, filterFaultEvents, filterByProject]);

  const historyGroups = React.useMemo(
    () =>
      filteredHistory.map(history => ({
        ...history,
        kind: "fault" as const,
        events: history.events,
      })),
    [filteredHistory],
  );

  if (faultEventsHistory.length === 0)
    return (
      <BlankState
        className={emptyBlankState}
        title={t("project.fault_event.side_panel.history.blank_state.title")}
        subtitle={t("project.fault_event.side_panel.history.blank_state.subtitle")}
        picto={<Dashboard size="l" />}
      />
    );

  return (
    <div className={sidePanelHistory}>
      <Filters
        className={filterWrapper}
        projectTypes={projectTypes}
        projectSites={projectSites}
        filterValues={filterValues}
        setFilterValues={setFilterValues}
      />
      {filteredHistory.length === 0 ? (
        <BlankState
          className={emptyBlankState}
          title={t("project.fault_event.side_panel.history.blank_state.no_events_found.title")}
          subtitle={t(
            "project.fault_event.side_panel.history.blank_state.no_events_found.subtitle",
          )}
          picto={<NoData size="l" />}
        />
      ) : (
        <HistoryContent histories={historyGroups} projectTimezone={project.timezone} />
      )}
    </div>
  );
};
export default SidePanelHistory;

//#region CSS
const sidePanelHistory = css`
  height: 100%;
`;

const filterWrapper = css`
  position: sticky;
  top: 105px;

  z-index: 0;

  background-color: ${colors.hydrogen};

  align-items: center;
  padding: ${spacings.m};
  border-bottom: 1px solid ${colors.titanium100};
`;

const emptyBlankState = css`
  padding: ${spacings.m};
  justify-content: flex-start;
`;
//#endregion CSS
